/**
 * Shared contract shapes (identity.yaml conventions, reused verbatim by
 * geo.yaml and merchants.yaml — §16.2). Hand-written mirrors of the merged
 * OpenAPI contracts: if a field is added there, it is added here.
 */

/** Crockford-base32 ULID (26 chars). */
export type Ulid = string;

/** Contract ULID pattern — Crockford base32 minus I/L/O/U, exactly 26 chars. */
export const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/** Decimal string, never a float — `^[0-9]+\.[0-9]{2,3}$` per contract. */
export type Money = string;

export const MONEY_PATTERN = /^[0-9]+\.[0-9]{2,3}$/;

/**
 * `VehicleType` — declared by drivers.yaml, and (M7-66/M7-69, owner ruling
 * 2026-09-05) referenced by merchants.yaml too: the merchant declares the
 * vehicle type their deliveries need, and dispatch offers an order only to
 * drivers whose single vehicle matches it.
 *
 * drivers.yaml **v1.5.0 (M7-150) stopped being an enum**: the vocabulary is
 * admin-managed rows in `vehicle_types`, so an operator can add a fifth code
 * at any time with no deploy. It is an OPAQUE UPPER_SNAKE string here on
 * purpose — the panel must never hard-code the four seeded codes, switch
 * exhaustively on them, or reject one it has not seen. Names come from
 * `GET /vehicle-types`; an unrecognised code renders as itself.
 */
export type VehicleType = string;

/** drivers.yaml `VehicleType` — the wire pattern, mirrored for form UX only. */
export const VEHICLE_TYPE_CODE_PATTERN = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*$/;
export const VEHICLE_TYPE_CODE_MAX = 32;
export const VEHICLE_TYPE_NAME_MAX = 60;

export interface GeoPoint {
  readonly lat: number;
  readonly lng: number;
}

export interface PageMeta {
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
}

export interface Paginated<T> {
  readonly data: ReadonlyArray<T>;
  readonly meta: PageMeta;
}

/** Standard list-query shape (server-side pagination — §11.2). */
export interface PageQuery {
  readonly page?: number;
  readonly per_page?: number;
}

/** Builds a query string from defined params only. */
export function queryString(params: Readonly<Record<string, string | number | boolean | undefined>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded === "" ? "" : `?${encoded}`;
}
